import type { IconName } from '@meterup/atto';
import { sizing } from '@meterup/atto';
import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  FLEXI_COLUMNS_ANIMATION_MS,
  FLEXI_COLUMNS_RESIZE_BOUNDS,
  FlexiColumnsColumn,
  FlexiColumnsColumnBody,
  FlexiColumnsContainer,
  FlexiColumnsDivider,
  FlexiColumnsOverlay,
  FlexiColumnsToggleButton,
} from './FlexiColumns.styles';
import { FlexiColumnsControls } from './FlexiColumnsControls';

export type FlexiColumnsActiveColumn = 'start' | 'end';

export interface FlexiColumnsContextValue {
  setActiveColumn: (pane: FlexiColumnsActiveColumn) => void;
  selectedId: string | null;
  onDeselect?: () => void;
  /** Called by a nested FlexiColumns to report its active column (null on unmount). */
  onNestedActiveColumnChange?: (column: FlexiColumnsActiveColumn | null) => void;
}

const FlexiColumnsContext = createContext<FlexiColumnsContextValue | null>(null);

export function useFlexiColumnsContext() {
  return useContext(FlexiColumnsContext);
}

export interface FlexiColumnContent {
  collapsible?: boolean;
  icon?: IconName;
  label: string;
  /**
   * Width of the collapsed peek strip in pixels. Defaults to `sizing.primary`. Set to 0 to hide.
   */
  peekWidth?: number;
  width?: number;
  render: ReactNode | ((props: { selectedId: string | null }) => ReactNode);
}

export interface FlexiColumnsProps {
  /**
   * Content for the start column (list, summary, etc.). Uses `FlexiColumns` context for selection.
   */
  start: FlexiColumnContent;
  /**
   * Renders the end column content for the selected item. Receives `selectedId` for the caller to resolve.
   */
  end: FlexiColumnContent;
  /**
   * When `selectedId` is `null`, which column to show.
   * Defaults to `'start'`. Use `'end'` when the end pane has standalone content (e.g. a form).
   */
  initialColumn?: FlexiColumnsActiveColumn;
  /**
   * Called when the selection is cleared (e.g. row deselect, delayed for animation).
   * Only used when the container is narrow.
   */
  onDeselect?: () => void;
  /**
   * Called when both panes visibility changes (e.g. collapse/expand on desktop).
   * Use with `useFlexiColumnsStorage` for persistence.
   */
  onBothPanesVisibleChange?: (visible: boolean) => void;
  /**
   * Called when start column width changes (after resize ends).
   * Use with `useFlexiColumnsStorage` for persistence.
   */
  onStartWidthChange?: (width: number) => void;
  /**
   * Column width preset: narrow-wide (start narrow), wide-narrow (end narrow), or even-split.
   * */
  preset: 'narrow-wide' | 'wide-narrow' | 'even-split';
  /**
   * Initial values from persisted storage. When provided, override defaults.
   */
  persistedState?: {
    bothPanesVisible?: boolean;
    startWidth?: number;
  };
  /**
   * Whether the columns are resizeable.
   */
  resizable?: boolean;
  /**
   * ID of the currently selected item. When set, the end column shows its detail.
   */
  selectedId?: string | null;
}

export function FlexiColumns({
  end: endProp,
  initialColumn = 'start',
  onBothPanesVisibleChange,
  onDeselect,
  onStartWidthChange,
  persistedState,
  preset,
  resizable = true,
  selectedId,
  start: startProp,
}: FlexiColumnsProps) {
  const start = { collapsible: startProp.collapsible ?? false, ...startProp };
  const end = { collapsible: endProp.collapsible ?? false, ...endProp };
  const parentContext = useFlexiColumnsContext();
  const location = useLocation();
  const locationRef = useRef(location.pathname + location.search);

  const [nestedActiveColumn, setNestedActiveColumn] = useState<FlexiColumnsActiveColumn | null>(
    null,
  );
  const handleNestedActiveColumnChange = useCallback((column: FlexiColumnsActiveColumn | null) => {
    setNestedActiveColumn(column);
  }, []);
  const endPaneRef = useRef<HTMLDivElement>(null);
  const startPaneRef = useRef<HTMLDivElement>(null);
  const previousActivePaneRef = useRef<FlexiColumnsActiveColumn>('start');
  const defaultStartWidth =
    preset === 'even-split'
      ? (FLEXI_COLUMNS_RESIZE_BOUNDS.min + FLEXI_COLUMNS_RESIZE_BOUNDS.max) / 2
      : start.width ?? end.width ?? 400;
  const initialStartWidth = persistedState?.startWidth ?? defaultStartWidth;
  const initialBothPanesVisible = persistedState?.bothPanesVisible ?? true;
  const [startWidth, setStartWidth] = useState(initialStartWidth);
  const [bothPanesVisible, setBothPanesVisible] = useState(initialBothPanesVisible);

  const isResizingRef = useRef(false);
  useEffect(() => {
    if (persistedState !== undefined && !isResizingRef.current) {
      setStartWidth(persistedState.startWidth ?? defaultStartWidth);
      setBothPanesVisible(persistedState.bothPanesVisible ?? true);
    }
  }, [persistedState, defaultStartWidth]);

  useEffect(() => {
    if (!resizable) {
      setStartWidth(defaultStartWidth);
    }
  }, [resizable, defaultStartWidth]);

  const resizeStartXRef = useRef<number>(0);
  const resizeStartWidthRef = useRef<number>(defaultStartWidth);

  const [activeColumn, setActiveColumn] = useState<FlexiColumnsActiveColumn>(() =>
    selectedId ? 'end' : initialColumn,
  );

  useEffect(() => {
    if (selectedId) {
      setActiveColumn('end');
      setBothPanesVisible(true);
    } else {
      setActiveColumn(initialColumn);
    }
  }, [selectedId, initialColumn]);

  useEffect(() => {
    if (!parentContext?.onNestedActiveColumnChange) return undefined;
    parentContext.onNestedActiveColumnChange(activeColumn);
    return () => parentContext.onNestedActiveColumnChange!(null);
  }, [parentContext, activeColumn]);

  useEffect(() => {
    const currentLocation = location.pathname + location.search;
    const previousLocation = locationRef.current;

    if (
      currentLocation !== previousLocation &&
      activeColumn === 'end' &&
      !selectedId &&
      initialColumn === 'start'
    ) {
      setActiveColumn('start');
    }

    locationRef.current = currentLocation;
  }, [location.pathname, location.search, activeColumn, selectedId, initialColumn]);

  useEffect(() => {
    setBothPanesVisible((prev) => {
      if (!prev) {
        const startCollapsed = activeColumn === 'end';
        const endCollapsed = activeColumn === 'start';
        if ((startCollapsed && !start.collapsible) || (endCollapsed && !end.collapsible)) {
          return true;
        }
      }
      return prev;
    });
  }, [activeColumn, start.collapsible, end.collapsible]);

  const { min: minStartWidth, max: maxStartWidth } = FLEXI_COLUMNS_RESIZE_BOUNDS;

  const latestStartWidthRef = useRef(startWidth);
  latestStartWidthRef.current = startWidth;

  const clampWidth = useCallback(
    (width: number) => Math.min(maxStartWidth, Math.max(minStartWidth, width)),
    [minStartWidth, maxStartWidth],
  );

  const resizeNarrowColumnSign = preset === 'wide-narrow' ? -1 : 1;

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (!resizable) return;
      e.preventDefault();
      (e.target as Element).setPointerCapture(e.pointerId);
      isResizingRef.current = true;
      resizeStartXRef.current = e.clientX;
      resizeStartWidthRef.current = startWidth;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - resizeStartXRef.current;
        setStartWidth(clampWidth(resizeStartWidthRef.current + delta * resizeNarrowColumnSign));
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
        isResizingRef.current = false;
        onStartWidthChange?.(latestStartWidthRef.current);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
    },
    [resizable, startWidth, clampWidth, onStartWidthChange, resizeNarrowColumnSign],
  );

  const RESIZE_STEP = 20;
  const RESIZE_STEP_LARGE = 60;

  const handleResizeByKeyboard = useCallback(
    (e: React.KeyboardEvent) => {
      if (!resizable) return;
      const isLarge = e.shiftKey;
      const step = isLarge ? RESIZE_STEP_LARGE : RESIZE_STEP;
      let delta = 0;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
      else if (e.key === 'Home') delta = minStartWidth - startWidth;
      else if (e.key === 'End') delta = maxStartWidth - startWidth;
      else return;
      e.preventDefault();
      const newWidth = clampWidth(startWidth + delta * resizeNarrowColumnSign);
      setStartWidth(newWidth);
      onStartWidthChange?.(newWidth);
    },
    [
      resizable,
      startWidth,
      minStartWidth,
      maxStartWidth,
      clampWidth,
      onStartWidthChange,
      resizeNarrowColumnSign,
    ],
  );

  const handleShowStart = useCallback(() => {
    setActiveColumn('start');
    setBothPanesVisible(true);
    onBothPanesVisibleChange?.(true);
  }, [onBothPanesVisibleChange]);

  const handleCollapseStart = useCallback(() => {
    if (!start.collapsible) return;
    setBothPanesVisible(false);
    setActiveColumn('end');
    onBothPanesVisibleChange?.(false);
  }, [start.collapsible, onBothPanesVisibleChange]);

  const handleCollapseEnd = useCallback(() => {
    if (!end.collapsible) return;
    setBothPanesVisible(false);
    setActiveColumn('start');
    onBothPanesVisibleChange?.(false);
  }, [end.collapsible, onBothPanesVisibleChange]);

  const handleShowEnd = useCallback(() => {
    setBothPanesVisible(true);
    setActiveColumn('end');
    onBothPanesVisibleChange?.(true);
  }, [onBothPanesVisibleChange]);

  // Escape key: return to start when in end pane
  useEffect(() => {
    if (activeColumn !== 'end') return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (nestedActiveColumn === 'end') return;
        handleShowStart();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeColumn, handleShowStart, nestedActiveColumn]);

  // Focus management when pane changes
  useLayoutEffect(() => {
    if (previousActivePaneRef.current === activeColumn) return undefined;

    previousActivePaneRef.current = activeColumn;

    if (activeColumn === 'end') {
      const timer = setTimeout(() => {
        const firstFocusable = endPaneRef.current?.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        firstFocusable?.focus();
      }, FLEXI_COLUMNS_ANIMATION_MS);
      return () => clearTimeout(timer);
    }

    if (activeColumn === 'start') {
      const timer = setTimeout(() => {
        const selectedRow = selectedId
          ? startPaneRef.current?.querySelector<HTMLElement>(
              `[data-item-id="${selectedId}"], [data-row-id="${selectedId}"]`,
            )
          : null;
        const firstRow = startPaneRef.current?.querySelector<HTMLElement>(
          '[data-item-id], [data-row-id]',
        );
        (selectedRow ?? firstRow)?.focus();
      }, FLEXI_COLUMNS_ANIMATION_MS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [activeColumn, selectedId]);

  const startPaneVisible = bothPanesVisible || activeColumn === 'start';
  const endPaneVisible = bothPanesVisible || activeColumn === 'end';
  const startPaneInteractive = activeColumn !== 'start' && !bothPanesVisible;
  const endPaneInteractive = activeColumn !== 'end' && !bothPanesVisible;
  const hasDivider = bothPanesVisible;
  const effectiveStartWidth = hasDivider && resizable ? startWidth : defaultStartWidth;

  const weightSum = FLEXI_COLUMNS_RESIZE_BOUNDS.min + FLEXI_COLUMNS_RESIZE_BOUNDS.max;
  const evenSplitWeight = Math.min(
    FLEXI_COLUMNS_RESIZE_BOUNDS.max,
    Math.max(FLEXI_COLUMNS_RESIZE_BOUNDS.min, effectiveStartWidth),
  );

  const containerCSS = useMemo(
    () => ({
      '--flexi-start-width': `${effectiveStartWidth}px`,
      '--flexi-start-fr': String(evenSplitWeight),
      '--flexi-end-fr': String(weightSum - evenSplitWeight),
      '--flexi-peek-width-start': `${start.peekWidth ?? sizing.primary}px`,
      '--flexi-peek-width-end': `${end.peekWidth ?? sizing.primary}px`,
      ...(start.width != null ? { '--flexi-fixed-start': `${start.width}px` } : {}),
      ...(end.width != null ? { '--flexi-fixed-end': `${end.width}px` } : {}),
    }),
    [
      effectiveStartWidth,
      evenSplitWeight,
      weightSum,
      start.peekWidth,
      end.peekWidth,
      start.width,
      end.width,
    ],
  );

  const contextValue = useMemo<FlexiColumnsContextValue>(
    () => ({
      setActiveColumn,
      selectedId: selectedId ?? null,
      onDeselect,
      onNestedActiveColumnChange: handleNestedActiveColumnChange,
    }),
    [selectedId, onDeselect, handleNestedActiveColumnChange],
  );

  const startContent =
    typeof start.render === 'function'
      ? start.render({ selectedId: selectedId ?? null })
      : start.render;
  const endContent =
    typeof end.render === 'function' ? end.render({ selectedId: selectedId ?? null }) : end.render;

  return (
    <FlexiColumnsContext.Provider value={contextValue}>
      <FlexiColumnsContainer
        css={containerCSS}
        data-active-column={activeColumn}
        data-both-panes-visible={bothPanesVisible}
        data-preset={preset}
        data-resizable={resizable}
        data-nested-active-column={nestedActiveColumn ?? undefined}
      >
        <FlexiColumnsColumn
          ref={startPaneRef}
          data-position="start"
          aria-hidden={!startPaneVisible}
          aria-expanded={startPaneVisible}
          aria-label={startPaneInteractive ? start.label : undefined}
          position="start"
          visible={startPaneVisible}
          role={startPaneInteractive ? 'button' : undefined}
          tabIndex={startPaneInteractive ? 0 : undefined}
          onClick={startPaneInteractive ? handleShowStart : undefined}
          onKeyDown={
            startPaneInteractive
              ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleShowStart();
                  }
                }
              : undefined
          }
          hasDivider={hasDivider && resizable}
        >
          <FlexiColumnsColumnBody data-flexi-slot="body">{startContent}</FlexiColumnsColumnBody>
        </FlexiColumnsColumn>
        {resizable && (
          <FlexiColumnsDivider data-flexi-divider>
            <FlexiColumnsControls
              onCollapseStart={handleCollapseStart}
              onCollapseEnd={handleCollapseEnd}
              onResizeStart={handleResizeStart}
              onResizeKeyDown={handleResizeByKeyboard}
              endLabel={end.label}
              startLabel={start.label}
              startWidth={effectiveStartWidth}
              startCollapsible={start.collapsible}
              endCollapsible={end.collapsible}
              resizable={resizable}
            />
          </FlexiColumnsDivider>
        )}
        <FlexiColumnsColumn
          ref={endPaneRef}
          data-position="end"
          aria-hidden={!endPaneVisible}
          aria-expanded={endPaneVisible}
          aria-label={endPaneInteractive ? end.label : undefined}
          position="end"
          visible={endPaneVisible}
          role={endPaneInteractive ? 'button' : undefined}
          tabIndex={endPaneInteractive ? 0 : undefined}
          onClick={endPaneInteractive ? handleShowEnd : undefined}
          onKeyDown={
            endPaneInteractive
              ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleShowEnd();
                  }
                }
              : undefined
          }
          hasDivider={hasDivider && resizable}
        >
          <FlexiColumnsColumnBody data-flexi-slot="body">{endContent}</FlexiColumnsColumnBody>
        </FlexiColumnsColumn>
        <FlexiColumnsOverlay data-flexi-overlay>
          <FlexiColumnsToggleButton
            data-toggle="start"
            position="start"
            appearance="vivid"
            variant="secondary"
            size="small"
            arrangement="hidden-label"
            icon={start.icon ?? 'chevron-right'}
            tooltipSide="right"
            aria-label={`Expand ${start.label.toLowerCase()}`}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleShowStart();
            }}
          >
            Expand {start.label.toLowerCase()}
          </FlexiColumnsToggleButton>
          <FlexiColumnsToggleButton
            data-toggle="end"
            position="end"
            appearance="vivid"
            variant="secondary"
            size="small"
            arrangement="hidden-label"
            icon={end.icon ?? 'chevron-left'}
            aria-label={`Expand ${end.label.toLowerCase()}`}
            tooltipSide="left"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleShowEnd();
            }}
          >
            Expand {end.label.toLowerCase()}
          </FlexiColumnsToggleButton>
        </FlexiColumnsOverlay>
      </FlexiColumnsContainer>
    </FlexiColumnsContext.Provider>
  );
}
